import { 
    applicantQuestions, 
    establishmentQuestions,
    policyQuestions,
    propertyQuestions,
    medicalmalpracticeQuestions,
    claimsAndInsuranceHistoryQuestions
} from '@helpers/questions';

// Helper functions
function getQuestionMap(policy) {
    const questions = policy?.questions || [];
    return questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});
}

export function getNamedValuesMap(policy) {
    return (policy?.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});
}

export function isAnsweredById(policy, question) {
    if (!question) return false;
    const answers = policy?.answers || {};
    return Boolean(answers[question.id]?.text_answer?.trim());
}

export function getAnswerText(policy, question) {
    if (!question) return '';
    const answers = policy?.answers || {};
    return answers[question.id]?.text_answer?.trim() || '';
}

// Rows of a Collection answer as [{ 'inner question text (lower-cased)': answer }]
export function getCollectionRows(policy, question) {
    if (!question || question.type !== 'Collection') return [];
    const raw = policy?.answers?.[question.id]?.text_answer;
    if (!raw) return [];
    let parsed;
    try { parsed = JSON.parse(raw); } catch (e) { return []; }
    const count = parseInt(parsed?.collections) || 0;
    const byUuid = {};
    (question.inner_questions || []).forEach(iq => { byUuid[iq.uuid] = (iq.question_text || '').trim().toLowerCase(); });
    const rows = [];
    for (let i = 0; i < count; i++) {
        const row = {};
        Object.entries(parsed.answers || {}).forEach(([uuid, list]) => {
            const label = byUuid[uuid];
            if (label) row[label] = (list?.[i]?.text_answer || '').trim();
        });
        rows.push(row);
    }
    return rows;
}

// Every additional location must be as complete as location one
// (same conditional logic as the flat questions on the Office Package page).
export function validateAdditionalLocations(policy, question) {
    const rows = getCollectionRows(policy, question);
    if (!rows.length) return false;
    const yes = (row, key) => (row[key] || '').toLowerCase() === 'yes';
    for (const row of rows) {
        const required = [
            'location address', 'contents limit', 'year of building construction', 'square footage',
            'building construction type', 'number of storeys', 'is the building more than 25 years old?',
            'distance to hydrants is less than 300m?', 'distance to firehall is less than 8km?',
            '100% sprinklered?', 'burglar alarm?',
            'do you require a mortgagee/loss payee added to this location?',
            'do you lease or own this premises?',
        ];
        if (yes(row, 'is the building more than 25 years old?')) required.push('roof', 'plumbing', 'heating', 'electrical');
        if (yes(row, 'do you require a mortgagee/loss payee added to this location?')) required.push('mortgagee/loss payee name', 'mortgagee/loss payee address');
        const owns = ['Own Building', 'Own Condo'].includes(row['do you lease or own this premises?'] || '');
        if (owns) required.push('do you wish to insure this building?');
        if (owns && yes(row, 'do you wish to insure this building?')) required.push('building limit');
        if (required.some(key => !row[key])) return false;
    }
    return true;
}

export function isAnsweredByIdCollections(policy, question) {
    if (!question) return false;
    if (question.type !== 'Collection') return false;

    const answers = policy?.answers || {};
    const answer = answers[question.id];

    if (!answer?.text_answer?.trim()) return false;

    try {
        const parsed = JSON.parse(answer.text_answer);
        const collectionsCount = parsed.collections || 0;

        // Must have at least one collection entry
        if (collectionsCount < 1) return false;

        const collectionAnswers = parsed.answers || {};

        // Check each required inner question has an answer for every collection row
        for (const innerQuestion of question.inner_questions || []) {
            if (!innerQuestion.required) continue;

            const innerAnswers = collectionAnswers[innerQuestion.uuid] || [];

            for (let i = 0; i < collectionsCount; i++) {
                const rowAnswer = innerAnswers[i];
                if (!rowAnswer?.text_answer?.trim()) {
                    return false;
                }
            }
        }

        return true;
    } catch (e) {
        return false;
    }
}

export function validateStart(policy) {
    const questionMap = getQuestionMap(policy);

    const startQuestion = questionMap['question example'];
    if (!isAnsweredById(policy, startQuestion)) return false;

    return true;
}

export function validateApplicant(policy) {
    const questionsList = applicantQuestions(policy);

    if (!isAnsweredById(policy, questionsList.start_date_question)) return false;
    if (!isAnsweredById(policy, questionsList.named_insured_question)) return false;
    if (!isAnsweredById(policy, questionsList.mailing_address_question)) return false;
    if (!isAnsweredById(policy, questionsList.contact_name_title_question)) return false;
    if (!isAnsweredById(policy, questionsList.email_question)) return false;
    if (!isAnsweredById(policy, questionsList.phone_number_question)) return false;
    if (!isAnsweredById(policy, questionsList.years_in_operation_question)) return false;
    if (!isAnsweredById(policy, questionsList.ccpa_member_question)) return false;
    if (!isAnsweredById(policy, questionsList.licensed_practitioner_question)) return false;
    if (!isAnsweredById(policy, questionsList.disciplinary_action_question)) return false;

    return true;
}

export function validateEstablishment(policy) {
    const questionsList = establishmentQuestions(policy);

    if (!isAnsweredById(policy, questionsList.sole_practitioner_or_clinic_question)) return false;
    const is_clinic = getAnswerText(policy, questionsList.sole_practitioner_or_clinic_question) === 'Clinic';
    if (is_clinic) {
        if (!isAnsweredById(policy, questionsList.how_many_licensed_practitioners_work_in_your_clinic_question)) return false;
    }
    if (!isAnsweredById(policy, questionsList.location_address_question)) return false;
    if (!isAnsweredByIdCollections(policy, questionsList.services_offered_question)) return false;
    if (!isAnsweredById(policy, questionsList.total_product_sales_percentage_question)) return false;
    if (!isAnsweredById(policy, questionsList.canadian_revenue_question)) return false;
    if (!isAnsweredById(policy, questionsList.usa_rest_of_world_revenue_question)) return false;
    if (!isAnsweredById(policy, questionsList.canadian_revenue_current_year_question)) return false;
    if (!isAnsweredById(policy, questionsList.usa_rest_of_world_revenue_current_year_question)) return false;
    if (!isAnsweredById(policy, questionsList.all_patients_treated_canadian_question)) return false;
    return true;
}

export function validatePolicy(policy) {
    const questionsList = policyQuestions(policy);

    // Always required 
    if (!isAnsweredById(policy, questionsList.please_indicate_which_insurance_coverage_you_are_applying_for_question)) return false;

    if (getAnswerText(policy, questionsList.please_indicate_which_insurance_coverage_you_are_applying_for_question) === 'None') return true;

    if (!isAnsweredById(policy, questionsList.commercial_general_liability_limits_of_insurance_question)) return false;

    const property_answer = getAnswerText(policy, questionsList.please_indicate_which_insurance_coverage_you_are_applying_for_question) === 'CGL & Property';
    if (property_answer) {
        if (!isAnsweredById(policy, questionsList.would_you_like_to_add_contents_coverage_question)) return false;
        const clinic_size = getNamedValuesMap(policy).clinic_size?.toLowerCase();
        if (questionsList.crime_limit_question && clinic_size !== '1-2') {
            if (!isAnsweredById(policy, questionsList.crime_limit_question)) return false;
        }
        if (!isAnsweredById(policy, questionsList.year_of_construction_question)) return false;
        if (!isAnsweredById(policy, questionsList.square_footage_question)) return false;
        if (!isAnsweredById(policy, questionsList.construction_question)) return false;
        if (!isAnsweredById(policy, questionsList.number_of_storeys_question)) return false;
        if (!isAnsweredById(policy, questionsList.building_more_than_25_years_old_question)) return false;

        if (getAnswerText(policy, questionsList.building_more_than_25_years_old_question) === 'yes') {
            if (!isAnsweredById(policy, questionsList.roof_question)) return false;
            if (!isAnsweredById(policy, questionsList.plumbing_question)) return false;
            if (!isAnsweredById(policy, questionsList.heating_question)) return false;
            if (!isAnsweredById(policy, questionsList.electrical_question)) return false;
        }

        if (!isAnsweredById(policy, questionsList.distance_to_hydrants_less_than_300m_question)) return false;
        if (!isAnsweredById(policy, questionsList.distance_to_firehall_less_than_8km_question)) return false;
        if (!isAnsweredById(policy, questionsList.percent_sprinkled_question)) return false;
        if (!isAnsweredById(policy, questionsList.burglar_alarm_question)) return false;
        if (!isAnsweredById(policy, questionsList.mortgagee_loss_payee_question)) return false;
        const mortgagee_loss_payee_answer = getAnswerText(policy, questionsList.mortgagee_loss_payee_question) === 'yes';
        if (mortgagee_loss_payee_answer) {
            if (!isAnsweredByIdCollections(policy, questionsList.name_and_address_of_mortgagee_loss_payee_question)) return false;
        }
        if (!isAnsweredById(policy, questionsList.do_you_lease_or_own_your_premises_question)) return false;
        const lease_or_own_premises_answer = getAnswerText(policy, questionsList.do_you_lease_or_own_your_premises_question) === 'Own Building' || getAnswerText(policy, questionsList.do_you_lease_or_own_your_premises_question) === 'Own Condo';
        if (lease_or_own_premises_answer) {
            if (!isAnsweredById(policy, questionsList.do_you_wish_to_insure_your_building_question)) return false;
        }
        const do_you_wish_to_insure_your_building_answer = getAnswerText(policy, questionsList.do_you_wish_to_insure_your_building_question) === 'yes';
        if (do_you_wish_to_insure_your_building_answer) {
            if (!isAnsweredById(policy, questionsList.building_limit_question)) return false;
        }
        if (!isAnsweredById(policy, questionsList.do_you_have_multiple_locations_question)) return false;
        if (getAnswerText(policy, questionsList.do_you_have_multiple_locations_question) === 'yes') {
            if (questionsList.additional_locations_question) {
                if (!validateAdditionalLocations(policy, questionsList.additional_locations_question)) return false;
            } else if (!isAnsweredById(policy, questionsList.please_list_all_locations_question)) return false;
        }
    }
    
    return true;
}

export function skipPropertyDetails(policy) {
    const questionsList = policyQuestions(policy);

    const property_answer = getAnswerText(policy, questionsList.please_indicate_which_insurance_coverage_you_are_applying_for_question) === 'CGL & Property';
    if (!property_answer) return true;

    return false;
}

export function skipMedMal(policy) {
    const questionsList = establishmentQuestions(policy);
    if (getAnswerText(policy, questionsList.sole_practitioner_or_clinic_question) === 'Sole Practitioner') return true;
    return false;
}

export function validatePropertyDetails(policy) {
    const questionsList = propertyQuestions(policy);

    if (getAnswerText(policy, questionsList.additional_insureds_question) === 'no') return true;
    if (!isAnsweredById(policy, questionsList.name_question)) return false;
    if (!isAnsweredById(policy, questionsList.address_question)) return false;
    

    return true;
}

export function validateMedMal(policy) {  
    const questionsList = medicalmalpracticeQuestions(policy);

    const no_medmal = skipMedMal(policy);
    if (no_medmal) return true;

    if (getAnswerText(policy, questionsList.medical_malpractice_coverage_insurance_limit_question) === 'I do not require Clinic Medical Malpractice Coverage') return true;
    if (!isAnsweredById(policy, questionsList.please_select_your_abuse_liability_coverage_limits_of_insurance_question)) return false;
    if (!isAnsweredById(policy, questionsList.is_there_a_written_policy_on_the_prevention_of_abuse_including_sexual_abuse_of_clients_patients_staff_question)) return false;
    if (!isAnsweredById(policy, questionsList.does_the_establishment_ensure_that_all_of_its_healthcare_staff_maintain_their_own_malpractice_liability_question)) return false;
    if (!isAnsweredById(policy, questionsList.as_part_of_the_practitioner_credentialing_process_is_evidence_of_this_coverage_required_on_an_annual_basis_question)) return false;
    if (!isAnsweredById(policy, questionsList.full_time_medical_question)) return false;
    if (!isAnsweredById(policy, questionsList.part_time_medical_question)) return false;
    if (!isAnsweredById(policy, questionsList.contract_medical_question)) return false;
    if (!isAnsweredById(policy, questionsList.full_time_nonmed_question)) return false;
    if (!isAnsweredById(policy, questionsList.part_time_nonmed_question)) return false;
    if (!isAnsweredById(policy, questionsList.contact_nonmed_question)) return false;
    if (!isAnsweredById(policy, questionsList.are_the_professional_licenses_or_certificates_of_all_employees_and_independent_contractors_verified_prior_to_their_employment_question)) return false;
    if (!isAnsweredById(policy, questionsList.is_informed_consent_obtained_prior_to_all_medical_procedures_treatments_tests_etc_question)) return false;
    if (!isAnsweredById(policy, questionsList.do_you_provide_any_services_to_any_pro_semi_pro_or_carded_athletes_question)) return false;
    if (!isAnsweredById(policy, questionsList.do_you_follow_the_current_guidance_for_infection_prevention_control_issued_by_the_public_health_agency_of_canada_or_provincial_health_agency_ie_ipac_sharps_safe_collection_storage_and_disposal_of_hazardous_waste_etc_question)) return false;
    if (!isAnsweredById(policy, questionsList.is_the_clinic_currently_participating_in_any_clinical_trials_question)) return false;
    return true;
}

export function validateClaims(policy) {
    const questionsList = claimsAndInsuranceHistoryQuestions(policy);

    if (!isAnsweredById(policy, questionsList.has_the_applicant_named_insured_suffered_any_claims_or_incidents_that_may_result_in_a_claim_prior_to_the_effective_date_of_this_proposed_policy_which_would_have_given_rise_to_a_claim_arising_from_your_professional_activities_in_the_past_year_question)) return false;
    if (!isAnsweredById(policy, questionsList.have_you_ever_been_declined_cancelled_or_non_renewed_by_any_insurer_question)) return false;
    if (!isAnsweredById(policy, questionsList.please_list_your_previous_insurer_s_question)) return false;
    let has_claim = getAnswerText(policy, questionsList.has_the_applicant_named_insured_suffered_any_claims_or_incidents_that_may_result_in_a_claim_prior_to_the_effective_date_of_this_proposed_policy_which_would_have_given_rise_to_a_claim_arising_from_your_professional_activities_in_the_past_year_question) === 'yes';
    if (has_claim) {
        if (!isAnsweredById(policy, questionsList.description_of_claim_question)) return false;
    }
    if (!isAnsweredById(policy, questionsList.do_you_currently_have_active_medical_malpractice_insurance_question)) return false;
    let has_medical_malpractice_insurance = getAnswerText(policy, questionsList.do_you_currently_have_active_medical_malpractice_insurance_question) === 'yes';
    if (has_medical_malpractice_insurance) {
        if (!isAnsweredById(policy, questionsList.retroactive_date_question)) return false;
    }
    // if (!isAnsweredById(policy, questionsList.higher_limits_question)) return false;
    return true;
}

